import { CommonModule } from '@angular/common';
import { NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { CompanyRolesComponent } from './../persons/components/company-roles/company-roles.component';

// Modules.
import { RouterModule } from '@angular/router';
import { NgbPopoverModule } from '@ng-bootstrap/ng-bootstrap';
import { CustomColumnDirective, GridComponent } from "@zing/grid";
import { PdfViewerModule } from 'ng2-pdf-viewer';
import { NgxMaskDirective, NgxMaskPipe, provideEnvironmentNgxMask } from 'ngx-mask';
import { TooltipModule } from 'primeng/tooltip';
import { TreeModule } from 'primeng/tree';
import { BusinessUnitListComponent } from '../business-unit/components/business-unit-list/business-unit-list.component';
import { BusinessUnitService } from '../business-unit/services/business-unit.service';
import {
  EmailMessagesContentComponent
} from '../communications/components/email-messages/email-messages-content/email-messages-content.component';
import { SmsMessagesContentComponent } from '../communications/components/sms-messages/sms-messages-content/sms-messages-content.component';
import { LocationListComponent } from '../locations/components/location-list/location-list.component';
import { AppraisalPermissionComponent } from '../persons/components/appraisal-permission/appraisal-permission.component';
import { AuctionPermissionComponent } from '../persons/components/auction-permission/auction-permission.component';
import {
  CommercialLinesPermissionComponent
} from '../persons/components/commercial-lines-permission/commercial-lines-permission.component';
import { JewelerPagesPermissionComponent } from '../persons/components/jeweler-pages-permission/jeweler-pages-permission.component';
import { LocationRolesComponent } from '../persons/components/location-roles/location-roles.component';
import { MarketPlacePermissionComponent } from '../persons/components/marketplace-permission/marketplace-permission.component';
import {
  PartnerAffiliationsComponent
} from '../persons/components/marketplace-permission/partner-affiliations/partner-affiliations.component';
import {
  SinglePartnerAffiliationComponent
} from '../persons/components/marketplace-permission/single-partner-affiliation/single-partner-affiliation.component';
import { PartnerGatewayPermissionComponent } from '../persons/components/partner-gateway-permission/partner-gateway-permission.component';
import { PersonDetailsComponent } from '../persons/components/person-details/person-details.component';
import { PersonIdentityComponent } from '../persons/components/person-identity/person-identity.component';
import { PersonListComponent } from '../persons/components/person-list/person-list.component';
import { PersonUpdatePasswordComponent } from '../persons/components/person-update-password/person-update-password.component';
import {
  PersonUpdateSecurityQuestionComponent
} from '../persons/components/person-update-security-question/person-update-security-question.component';
import {
  PersonalInsurancePermissionComponent
} from '../persons/components/personal-insurance-permission/personal-insurance-permission.component';
import { PlatformRolesComponent } from '../persons/components/platform-roles/platform-roles.component';
import { ShipmentPermissionsComponent } from '../persons/components/shipment-permissions/shipment-permissions.component';
import { PersonService } from '../persons/services/person.service';
import { ScreeningResultsTableModule } from '../screening/components/screening-results-table/screening-results-table.module';
import { UserRoleService } from '../security/services/user-role.service';
import { AddressComponent } from './components/address/address.component';
import { ApiLogDetailsComponent } from './components/api-logs/api-log-details/api-log-details.component';
import { ApiLogListComponent } from './components/api-logs/api-log-list/api-log-list.component';
import {
  ActivateDeactivateConfirmationAddComponent
} from './components/dialogs/activate-deactivate-confirmation-add/activate-deactivate-confirmation-add.component';
import {
  ActivateDeactivateConfirmationComponent
} from './components/dialogs/activate-deactivate-confirmation/activate-deactivate-confirmation.component';
import {
  BaseConfirmationWithCommentComponent
} from './components/dialogs/base-confirmation-with-comment/base-confirmation-with-comment.component';
import { DeleteBusinessUnitWarningComponent } from './components/dialogs/business-unit/delete-business-unit-warning.component';
import { CompanyLinkingComponent } from './components/dialogs/company-linking/company-linking.component';
import { ConfirmationDialogComponent } from './components/dialogs/confirmation-dialog/confirmation-dialog.component';
import { ConfirmationWarningDialogComponent } from './components/dialogs/confirmation-warning-dialog/confirmation-warning-dialog.component';
import {
  ConfirmationWithCommentDialogComponent
} from './components/dialogs/confirmation-with-comment-dialog/confirmation-with-comment-dialog.component';
import {
  ConfirmationWithCommentWarningDialogComponent
} from './components/dialogs/confirmation-with-comment-warning-dialog/confirmation-with-comment-warning-dialog.component';
import {
  DeactivateConfirmationWithCommentComponent
} from './components/dialogs/deactivate-confirmation-with-comment/deactivate-confirmation-with-comment.component';
import {
  DeleteConfirmationWithCommentComponent
} from './components/dialogs/delete-confirmation-with-comment/delete-confirmation-with-comment.component';
import { DeleteConfirmationComponent } from './components/dialogs/delete-confirmation/delete-confirmation.component';
import { EmailSmsPreviewDialogComponent } from './components/dialogs/email-sms-preview-dialog/email-sms-preview-dialog.component';
import { ForbiddenDialogComponent } from './components/dialogs/forbidden-dialog/forbidden-dialog.component';
import { InvitesDialogComponent } from './components/dialogs/invites/invites-dialog.component';
import { OrderStatusReasonsComponent } from './components/dialogs/order-status-reasons-dialog/order-status-reasons-dialog';
import { PdfUploadDialogComponent } from './components/dialogs/pdf-upload-dialog/pdf-upload-dialog.component';
import { PersonInfoModalComponent } from './components/dialogs/person-info-modal/person-info-modal.component';
import { PublishConfirmationComponent } from './components/dialogs/publish-confirmation.component';
import { TemplatePreviewDialogComponent } from './components/dialogs/template-preview-dialog/template-preview-dialog.component';
import {
  GenerateReportDialogSimpleComponent
} from './components/generate-report-dialog-simple/generate-report-dialog-simple.component';
import { GenerateReportComponent } from './components/generate-report/generate-report.component';
import { HealthCheckDetailsComponent } from './components/health-check/components/health-check-details/health-check-details.component';
import { HealthCheckComponent } from './components/health-check/components/health-check/health-check.component';
import { InProgressModule } from './components/in-progress.module';
import { ReportSummaryCardComponent } from './components/report-summary-card/report-summary-card.component';
import { SimplePaginationComponent } from './components/simple-pagination/simple-pagination.component';
import { ApiStatusDetailComponent } from './components/status-details/api-status-details';
import { CompanyAutocompleteComponent } from './components/company-autocomplete/company-autocomplete.component';
import { FormatDecimalsDirective } from './directives/format-decimals.directive';
import { AddressPipe } from './pipes/address.pipe';
import { ApprovalStatusPipe } from './pipes/approval-status.pipe';
import { BooleanToYesNoPipe } from './pipes/boolean-to-yes-no.pipe';
import { CurrencyExchangePipe } from './pipes/currency-exchange.pipe';
import { KeysPipe } from './pipes/keys.pipe';
import { PhoneMaskPipe } from './pipes/phone-mask.pipe';
import { ServiceModuleNamePipe } from './pipes/service-module-name.pipe';
import { TruncateStringPipe } from './pipes/truncate-string.pipe';
import { ApiLogsService } from './services/api-logs.service';
import { AnalyticsService } from './services/app-insights.service';
import { BaseHttpService } from './services/base-http.service';
import { BulkReInviteProcessService } from './services/bulk-reinvite-process.service';
import { InviteService } from './services/invite.service';
import { LocationService } from './services/location.service';
import { PartnerService } from './services/partner.service';
import { ProfileService } from './services/profile.service';
import { ReportingService } from './services/reporting.service';
import { ShippingService } from './services/shipping.service';
import { VerificationService } from './services/verification.service';
import { SharedMaterialModule } from './shared-material.module';

@NgModule({
  imports: [
    NgbPopoverModule,
    FormsModule,
    ReactiveFormsModule,
    SharedMaterialModule,
    CommonModule,
    PdfViewerModule,
    InProgressModule,
    RouterModule,
    ScreeningResultsTableModule,
    NgxMaskDirective,
    NgxMaskPipe,
    GridComponent,
    TreeModule,
    TooltipModule
  ],
  declarations: [
    AddressComponent,
    HealthCheckComponent,
    HealthCheckDetailsComponent,
    ApiStatusDetailComponent,
    ApiLogListComponent,
    ApiLogDetailsComponent,
    PersonListComponent,
    DeleteConfirmationComponent,
    ConfirmationWarningDialogComponent,
    DeleteConfirmationWithCommentComponent,
    ConfirmationWithCommentWarningDialogComponent,
    DeleteBusinessUnitWarningComponent,
    PublishConfirmationComponent,
    InvitesDialogComponent,
    CompanyLinkingComponent,
    LocationListComponent,
    BusinessUnitListComponent,
    KeysPipe,
    AddressPipe,
    TruncateStringPipe,
    CurrencyExchangePipe,
    PhoneMaskPipe,
    ServiceModuleNamePipe,
    PersonDetailsComponent,
    PlatformRolesComponent,
    LocationRolesComponent,
    CompanyRolesComponent,
    PersonIdentityComponent,
    PersonUpdatePasswordComponent,
    PersonUpdateSecurityQuestionComponent,
    ShipmentPermissionsComponent,
    AuctionPermissionComponent,
    CommercialLinesPermissionComponent,
    PersonalInsurancePermissionComponent,
    MarketPlacePermissionComponent,
    ConfirmationDialogComponent,
    PartnerGatewayPermissionComponent,
    JewelerPagesPermissionComponent,
    AppraisalPermissionComponent,
    GenerateReportDialogSimpleComponent,
    GenerateReportComponent,
    ReportSummaryCardComponent,
    ApprovalStatusPipe,
    BooleanToYesNoPipe,
    PartnerAffiliationsComponent,
    SinglePartnerAffiliationComponent,
    FormatDecimalsDirective,
    ActivateDeactivateConfirmationComponent,
    ActivateDeactivateConfirmationAddComponent,
    EmailSmsPreviewDialogComponent,
    TemplatePreviewDialogComponent,
    PdfUploadDialogComponent,
    EmailMessagesContentComponent,
    SmsMessagesContentComponent,
    OrderStatusReasonsComponent,
    ForbiddenDialogComponent,
    SimplePaginationComponent,
    DeactivateConfirmationWithCommentComponent,
    BaseConfirmationWithCommentComponent,
    ConfirmationWithCommentDialogComponent,
    CompanyAutocompleteComponent,
    PersonInfoModalComponent
  ],
  exports: [
    CommonModule,
    RouterModule,
    FormsModule,
    ReactiveFormsModule,
    SharedMaterialModule,
    AddressComponent,
    HealthCheckComponent,
    HealthCheckDetailsComponent,
    PersonListComponent,
    LocationListComponent,
    BusinessUnitListComponent,
    NgbPopoverModule,
    KeysPipe,
    AddressPipe,
    TruncateStringPipe,
    PhoneMaskPipe,
    ServiceModuleNamePipe,
    CurrencyExchangePipe,
    PersonDetailsComponent,
    GenerateReportComponent,
    ReportSummaryCardComponent,
    ApprovalStatusPipe,
    BooleanToYesNoPipe,
    FormatDecimalsDirective,
    EmailMessagesContentComponent,
    SmsMessagesContentComponent,
    InProgressModule,
    ScreeningResultsTableModule,
    NgxMaskDirective,
    NgxMaskPipe,
    SimplePaginationComponent,
    DeactivateConfirmationWithCommentComponent,
    BaseConfirmationWithCommentComponent,
    GridComponent,
    CustomColumnDirective,
    ConfirmationWithCommentDialogComponent,
    CompanyAutocompleteComponent
  ],
  providers: [
    BaseHttpService,
    ApiLogsService,
    PersonService,
    ProfileService,
    PartnerService,
    ShippingService,
    VerificationService,
    InviteService,
    LocationService,
    BusinessUnitService,
    UserRoleService,
    AnalyticsService,
    ReportingService,
    BulkReInviteProcessService,
    provideEnvironmentNgxMask()
  ]
})
export class SharedModule {
  constructor() {
  }
}
